import { scroll, animate, stagger } from 'vendor';
import { matchesMediaQuery, debounce } from 'theme';

/*
 * Farida Gupta - drives the pinned hero's heading fade/translate and background zoom off real
 * scroll progress via Motion One's scroll() timeline - the same mechanism already used by
 * fg-image-with-text-overlay-reveal.js for sections/image-with-text-overlay.liquid - instead of a
 * bespoke rAF/IntersectionObserver scroll listener.
 */

/*
 * The scroll fraction (of the pin's actual pixel range, see setupScrollAnimations) at which the
 * first content block is fully gone and the second one starts taking over - shared by the
 * position/reveal offsets below. Pointer-events for both blocks are derived from their real
 * rendered opacity instead (see the pointer-events toggle below), not from this fraction, so they
 * can't drift out of sync with what's actually on screen the way they did before.
 */
const HANDOFF_FRACTION = 0.5;

class FgHeroScrollFade extends HTMLElement {
  connectedCallback() {
    if (!matchesMediaQuery('motion-safe')) return;

    this.image = this.querySelector('.fg-hero-scroll-fade__image');
    this.content = this.querySelector('.fg-hero-scroll-fade__content');
    this.secondContent = this.querySelector('.fg-hero-scroll-fade__content--second');

    if (!this.content) return;

    this.scrollCleanups = [];
    this.setupScrollAnimations();

    /*
     * The handoff point is a pixel offset within the pin's actual scroll range, not a percentage
     * of this element's full height, so it (and the scroll trackers built from it) has to be
     * recomputed whenever the viewport - and therefore the pin range - changes size.
     */
    this.onResize = debounce(() => this.setupScrollAnimations(), 200);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.teardownScrollAnimations();
  }

  teardownScrollAnimations() {
    this.scrollCleanups?.forEach((cleanup) => cleanup());
    this.scrollCleanups = [];
  }

  setupScrollAnimations() {
    this.teardownScrollAnimations();

    /*
     * Percentage offsets (e.g. '50% start') are measured against this element's full height
     * (the "scroll_length" multiplier x 100vh), not against the shorter distance it's actually
     * pinned for (height - one viewport) - so the handoff is computed in real pixels instead.
     */
    const pinRange = this.offsetHeight - window.innerHeight;
    const handoff = pinRange * HANDOFF_FRACTION;
    /*
     * scroll() always stretches an animation's full timeline across exactly the offset range
     * given to it - so an offset ending at 'end end' finishes precisely when the panel unpins,
     * leaving no time for the fully-revealed second content (in particular its button) to
     * actually be clicked before the next section scrolls over it. Ending at 85% of the pin
     * range instead leaves a settle buffer where everything is finished revealing and still
     * pinned/interactive.
     */
    const revealEnd = pinRange * 0.85;

    /*
     * FIRST CONTENT
     *
     * Fades out and drifts upward, fully gone by the handoff point, making way for the second
     * content block (if any).
     */
    this.scrollCleanups.push(
      scroll(
        animate(
          this.content,
          {
            opacity: [1, 0],
            transform: ['translateY(0)', 'translateY(-40px)'],
          },
          {
            easing: 'linear',
          }
        ),
        {
          target: this,
          offset: ['start start', `${handoff}px start`],
        }
      )
    );

    if (this.secondContent) {
      /*
       * SECOND CONTENT - POSITION
       *
       * The block's top edge starts 30% down the panel. It ends with its BOTTOM edge 20% up
       * from the panel's bottom edge - not just its top edge at the 80% mark, which for a tall
       * block would push the actual bottom further down than that 20% clearance - so the block's
       * own rendered height (blockHeight) is subtracted from the 80% mark to compensate.
       * Computed from real rendered heights rather than CSS top/bottom percentages, since the
       * block is positioned with transform (composes cleanly with the per-child stagger below)
       * instead of layout properties - place-self-start (sections/fg-hero-scroll-fade.liquid)
       * anchors it at the panel's top-left as the transform's zero point.
       */
      const contentOverMedia = this.querySelector('.content-over-media');
      const panelHeight = contentOverMedia ? contentOverMedia.offsetHeight : window.innerHeight;
      const blockHeight = this.secondContent.offsetHeight;
      const positionStart = panelHeight * 0.3;
      const positionEnd = panelHeight * 0.8 - blockHeight;

      this.scrollCleanups.push(
        scroll(
          animate(
            this.secondContent,
            {
              transform: [`translateY(${positionStart}px)`, `translateY(${positionEnd}px)`],
            },
            {
              easing: 'linear',
            }
          ),
          {
            target: this,
            offset: [`${handoff}px start`, `${revealEnd}px start`],
          }
        )
      );

      /*
       * SECOND CONTENT - REVEAL
       *
       * The eyebrow, heading, excerpt, and button reveal one after another (stagger(0.1), each
       * over duration: 0.3) instead of fading in together as one block, so the block reads as a
       * considered sequence rather than everything appearing in a cluttered clump. The staggered
       * group starts right at the handoff and finishes at revealEnd (85% of the pin range, not
       * 'end end') - the button, last in the sequence, needs to actually finish fading in while
       * still pinned and interactive, not right as the panel unpins. Each child's own small
       * translateY composes with the container's bigger one above.
       */
      const secondContentChildren = [
        this.secondContent.querySelector('.fg-hero-scroll-fade__eyebrow'),
        this.secondContent.querySelector('.fg-hero-scroll-fade__second-heading'),
        this.secondContent.querySelector('.fg-hero-scroll-fade__second-subheading'),
        this.secondContent.querySelector('.fg-hero-scroll-fade__second-button'),
      ].filter(Boolean);

      if (secondContentChildren.length > 0) {
        this.scrollCleanups.push(
          scroll(
            animate(
              secondContentChildren,
              {
                opacity: [0, 1],
                // Bottom-anchored now (place-self: end start), so it should slide down into its
                // resting spot, not up into it - starts above rest (-30px) and settles at 0.
                transform: ['translateY(-30px)', 'translateY(0)'],
              },
              {
                delay: stagger(0.1),
                duration: 0.3,
                easing: 'linear',
              }
            ),
            {
              target: this,
              offset: [`${handoff}px start`, `${revealEnd}px start`],
            }
          )
        );
      }

      /*
       * Both blocks sit in the same content-over-media grid cell, so once one has fully faded
       * out it still overlaps the other and would otherwise stay clickable/focusable. This used
       * to toggle pointer-events off a second, independently-computed scroll progress - which
       * drifted out of sync with the opacity animations above and left the second block stuck at
       * pointer-events: none even once fully visible. Reading each block's own real rendered
       * opacity here instead means the two literally cannot disagree: the second block has no
       * opacity of its own (only its children animate, staggered), so its last child - the last
       * one to finish fading in - stands in for "is the whole block done revealing".
       */
      const content = this.content;
      const secondContent = this.secondContent;
      const lastSecondContentChild = secondContentChildren[secondContentChildren.length - 1];

      this.scrollCleanups.push(
        scroll(
          () => {
            const contentOpacity = Number(getComputedStyle(content).opacity);
            content.style.pointerEvents = contentOpacity > 0.05 ? '' : 'none';

            const secondContentOpacity = lastSecondContentChild
              ? Number(getComputedStyle(lastSecondContentChild).opacity)
              : 0;
            secondContent.style.pointerEvents = secondContentOpacity > 0.05 ? '' : 'none';
          },
          {
            target: this,
            offset: ['start start', 'end end'],
          }
        )
      );
    }

    if (!this.image) return;

    /*
     * BACKGROUND
     *
     * Subtle Ken Burns zoom across the pin's full scroll range.
     */
    this.scrollCleanups.push(
      scroll(
        animate(
          this.image,
          {
            transform: ['scale(1)', 'scale(1.12)'],
          },
          {
            easing: 'linear',
          }
        ),
        {
          target: this,
          offset: ['start start', 'end end'],
        }
      )
    );
  }
}

if (!window.customElements.get('fg-hero-scroll-fade')) {
  window.customElements.define('fg-hero-scroll-fade', FgHeroScrollFade);
}
